import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Link as MuiLink,
    Button,
    Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid2";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { defaultVariables } from "../../../../config";
import { getMediaURL } from "../../../../config/values";
import LoadingButton from "../../../components/elements/LoadingButton";
import AppCardLoaderFields from "../../../components/loaders/AppCardLoaderFields";
import StaticMap from "../../../components/address/map/StaticMap";
import SubTitle from "../../../components/elements/SubTitle";
import { Link } from "react-router-dom";
import VendorLeadEditDialog from "./VendorLeadEditDialog";
import { Pencil } from "lucide-react";
const VendorLeadDetailDialog = ({ leadId, onClose, onApproved, onUpdated }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [lead, setLead] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const loginToken = localStorage.getItem("ACT");
    const config = {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        },
    };

    const loadData = () => {
        setLoading(true);
        axios
            .get(`${defaultVariables.url}/vendor-leads/get/${leadId}`, config)
            .then((response) => {
                setLead(response.data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        loadData();
    }, [leadId]);

    const handleApprove = async () => {
        setSubmitLoading(true);
        try {
            await axios.post(`${defaultVariables.url}/vendor-leads/approve`, { id: leadId }, config);
            setConfirmOpen(false);
            onApproved();
        } catch (error) {
            const message = error.response?.data?.message;
            if (message === "mobile_exists" || message === "email_exists") {
                enqueueSnackbar(t("marketingApproveDuplicate"), { variant: "error" });
            } else if (message === "already_approved") {
                enqueueSnackbar(t("marketingAlreadyApproved"), { variant: "warning" });
            } else {
                enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleResendCredentials = async () => {
        setResendLoading(true);
        try {
            await axios.post(`${defaultVariables.url}/vendor-leads/resend-credentials`, { id: leadId }, config);
            setResendConfirmOpen(false);
            enqueueSnackbar(t("marketingResendCredentialsSuccess"), { variant: "success" });
        } catch (error) {
            const message = error.response?.data?.message;
            if (message === "not_approved") {
                enqueueSnackbar(t("marketingResendCredentialsNotApproved"), { variant: "warning" });
            } else {
                enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
            }
        } finally {
            setResendLoading(false);
        }
    };

    const repName = lead?.marketing_representative
        ? `${lead.marketing_representative.first_name} ${lead.marketing_representative.last_name}`
        : "-";

    return (
        <>
            <Dialog open onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t("marketingLeadDetails")}
                    <IconButton onClick={onClose} style={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loading || !lead ? (
                        <AppCardLoaderFields />
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item size={12}>
                                <Typography color="textSecondary">
                                    {t("marketingRepName")}: <strong>{repName}</strong>
                                </Typography>
                                <Typography color="textSecondary">
                                    {t("status")}: {t(`marketingLeadStatus_${lead.status}`)}
                                </Typography>
                            </Grid>

                            <Grid item size={{ xs: 12, md: 6 }}>
                                <SubTitle align="left">{t("first_name")}</SubTitle>
                                <Typography>{lead.owner_first_name}</Typography>
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <SubTitle align="left">{t("last_name")}</SubTitle>
                                <Typography>{lead.owner_last_name}</Typography>
                            </Grid>
                            <Grid item size={12}>
                                <SubTitle align="left">{t("marketingShopName")}</SubTitle>
                                <Typography>{lead.shop_name}</Typography>
                            </Grid>
                            <Grid item size={12}>
                                <SubTitle align="left">{t("industry")}</SubTitle>
                                <Typography>{t(lead.industry)}</Typography>
                            </Grid>
                            <Grid item size={12}>
                                <SubTitle align="left">{t("marketingTcKimlik")}</SubTitle>
                                <Typography dir="ltr">{lead.tc_kimlik_no}</Typography>
                            </Grid>
                            <Grid item size={12}>
                                <SubTitle align="left">{t("marketingStoreAddress")}</SubTitle>
                                <Typography>{lead.address_text}</Typography>
                            </Grid>
                            {!!lead.lat && !!lead.lng && (
                                <Grid item size={12}>
                                    <StaticMap height={220} coordinates={{ lat: parseFloat(lead.lat), lng: parseFloat(lead.lng) }} />
                                </Grid>
                            )}
                            <Grid item size={12}>
                                <SubTitle align="left">{t("marketingVergiNumarasi")}</SubTitle>
                                <Typography dir="ltr">{lead.vergi_numarasi}</Typography>
                            </Grid>
                            {!!lead.tax_document && (
                                <Grid item size={12}>
                                    <SubTitle align="left">{t("marketingVergiLevhasi")}</SubTitle>
                                    <MuiLink href={getMediaURL(lead.tax_document)} target="_blank" rel="noopener">
                                        {t("marketingViewDocument")}
                                    </MuiLink>
                                </Grid>
                            )}
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <SubTitle align="left">{t("mobile")}</SubTitle>
                                <Typography dir="ltr">{lead.mobile}</Typography>
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <SubTitle align="left">{t("marketingTelephone")}</SubTitle>
                                <Typography dir="ltr">{lead.telephone || "-"}</Typography>
                            </Grid>
                            <Grid item size={12}>
                                <SubTitle align="left">{t("email")}</SubTitle>
                                <Typography>{lead.email}</Typography>
                            </Grid>

                            {lead.status === "approved" && !!lead.created_shop_id && (
                                <Grid item size={12}>
                                    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                                        <Button
                                            component={Link}
                                            to={`/panel/manage/shops/edit/${lead.created_shop_id}`}
                                            variant="outlined"
                                            color="primary"
                                            size="medium"
                                            sx={{
                                                textTransform: "none",
                                                minHeight: 40,
                                                px: 2.75,
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            {t("marketingViewShop")}
                                        </Button>
                                        {!!lead.created_user_id && (
                                            <LoadingButton
                                                variant="outlined"
                                                color="primary"
                                                size="medium"
                                                loading={resendLoading}
                                                styleForParent={{ margin: 0, display: "inline-flex" }}
                                                style={{
                                                    textTransform: "none",
                                                    minHeight: 40,
                                                    padding: "8px 22px",
                                                    fontSize: "0.875rem",
                                                }}
                                                onClick={() => setResendConfirmOpen(true)}
                                            >
                                                {t("marketingResendCredentials")}
                                            </LoadingButton>
                                        )}
                                    </Stack>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    {!!lead && lead.status === "pending" && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<Pencil size={16} />}
                            onClick={() => setEditOpen(true)}
                        >
                            {t("edit")}
                        </Button>
                    )}
                    {!!lead && lead.status === "pending" && (
                        <LoadingButton
                            variant="contained"
                            color="secondary"
                            loading={submitLoading}
                            onClick={() => setConfirmOpen(true)}
                        >
                            {t("marketingApproveLead")}
                        </LoadingButton>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>{t("marketingApproveLead")}</DialogTitle>
                <DialogContent>
                    <Typography>{t("marketingApproveConfirm")}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>{t("back")}</Button>
                    <LoadingButton variant="contained" color="secondary" loading={submitLoading} onClick={handleApprove}>
                        {t("confirm")}
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <VendorLeadEditDialog
                leadId={leadId}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={() => {
                    loadData();
                    onUpdated && onUpdated();
                }}
            />

            <Dialog open={resendConfirmOpen} onClose={() => setResendConfirmOpen(false)}>
                <DialogTitle>{t("marketingResendCredentials")}</DialogTitle>
                <DialogContent>
                    <Typography>{t("marketingResendCredentialsConfirm")}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResendConfirmOpen(false)}>{t("back")}</Button>
                    <LoadingButton variant="contained" color="primary" loading={resendLoading} onClick={handleResendCredentials}>
                        {t("confirm")}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VendorLeadDetailDialog;
